import { SiteUrls } from '@tableau/shared-urls';
import { SagaIterator } from 'redux-saga';
import { takeEvery } from 'redux-saga/effects';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { Location } from '~commonInfra/utils/Location';
import { UploadBundlesActionsTypes } from '~src/uploadBundles/actions/UploadBundlesActions';
import { uploadBundles } from '~src/uploadBundles/sagas/UploadBundlesSaga';
import { fetchWorkbookById } from '~src/workbook/sagas/WorkbookSagasHelper';

import { SessionSiteSelectors } from '../../bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '../../bootstrap/redux/store/AppStore';
import { Messages } from '../../codegen/Localize';
import { ReactModalService } from '../../commonInfra/dialogs/ReactModalService';
import { getErrorSourceFromRestError, RestErrorSource } from '../../httpClient/RestApiService';
import { Projects } from '../../projects/resources/Projects';
import { PublishWorkbookAction } from '../actions/PublishWorkbookAction';
import {
  removeUploadContentByIdAction,
  requestUploadContentCompleteAction,
  requestUploadContentInProgressAction,
} from '../actions/UploadContentProgressActions';
import { requestUploadFlowAction, UploadFlowActionsTypes, UploadFlowStartAction } from '../actions/UploadFlowActions';
import { requestUploadWorkbookAction, UploadWorkbookActionsTypes, UploadWorkbookStartAction } from '../actions/UploadWorkbookActions';
import { UploadFlowDialogOpts } from '../components/ConnectedUploadFlowDialog';
import { UploadProgressDialogOpts } from '../components/ConnectedUploadProgressDialog';
import { UploadWorkbookDialogOpts } from '../components/ConnectedUploadWorkbookDialog';
import { OverwriteConfirmDialogOpts } from '../components/OverwriteConfirmDialog';
import { UploadFlowErrorCodes } from '../restApi/FlowUpload';
import { UploadWorkbookErrorCodes, WorkbookUploadResponse, WorkbookView } from '../restApi/WorkbookUpload';
import { createCancelToken } from '../utils/UploadContentRequestHelper';
import UploadFlowRequestHelper from '../utils/UploadFlowRequestHelper';
import { sendUploadFlowActionTelemetry } from '../utils/UploadFlowTelemetryEvents';
import UploadWorkbookRequestHelper from '../utils/UploadWorkbookRequestHelper';
import { sendUploadWorkbookActionTelemetry } from '../utils/UploadWorkbookTelemetryEvents';

export async function uploadWorkbook(action: UploadWorkbookStartAction) {
  const toaster = ToasterService.instance;

  const id = `${action.data.projectId}-${action.data.name}`;
  const progressDispatcher = generateUpdateUploadProgressHandler(id);
  const cancelToken = createCancelToken();
  const cancelHandler = () => { cancelToken.cancel('user cancelled'); };

  const projectLuid = await getProjectLuid(action.data.projectId);
  const fileSize = action.data.file.size;
  const fileName = action.data.file.name;

  const publishingMessage = Messages.contentUpload_uploadWorkbookNameProgressPublishing();
  const progressDialog = ReactModalService.openCustom(UploadProgressDialogOpts(id, fileSize, fileName, publishingMessage, cancelHandler));

  let isPublishSuccessful: boolean;
  let errorMsg: string;
  let uploadStartTime: any;
  let uploadEndTime: any;

  try {
    uploadStartTime = Date.now();
    const result = await UploadWorkbookRequestHelper.uploadWorkbookRequest(action, projectLuid, progressDispatcher, cancelToken);
    getAppStore().dispatch(requestUploadContentCompleteAction({ id }));
    uploadEndTime = Date.now();
    toaster.pop({
      type: ToastType.Success,
      text: Messages.workbookUploadSuccessMessage(),
      keepOnLocationChange: true,
    });
    let destinationUrlAfterUpload = getDefaultViewUrl(result);
    const workbookId = parseWorkbookIdFromWebpageUrl(result.workbook.webpageUrl);
    if (await UploadWorkbookRequestHelper.workbookUsesAnalyticsExtension(workbookId)) {
      const isWorkbookAvailable = await UploadWorkbookRequestHelper.isWorkbookAvailableInDatabase(workbookId);
      if (isWorkbookAvailable) {
        destinationUrlAfterUpload = generateSiteUrls().workbookViewsUrl(workbookId);
        PublishWorkbookAction.handlePublishedWorkbook(await fetchWorkbookById(workbookId), '/');
      }
    }
    Location.instance.setUrl(destinationUrlAfterUpload);
    getAppStore().dispatch(removeUploadContentByIdAction({ id }));
    isPublishSuccessful = true;
  } catch (error) {
    progressDialog.close();
    getAppStore().dispatch(removeUploadContentByIdAction({ id }));
    if (getErrorSourceFromRestError(error) !== RestErrorSource.Cancelled) {
      switch(error.data?.error?.code) {
        case String(UploadWorkbookErrorCodes.PUBLISHING_OVERWRITE):
            showConfirmOverwriteDialog();
            break;
        default:
            showErrorToast(error);
            break;
      }
      isPublishSuccessful = false;
    }
  }

  function showErrorToast(error: any) {
    const toastId = toaster.pop({
      type: ToastType.Error,
      text: UploadWorkbookRequestHelper.getErrorMessage(error),
      actionMessage: UploadWorkbookRequestHelper.getActionMessage(error),
      action: () => {
        if (!ReactModalService.isAnyModalOpen()) {
          ReactModalService.openCustom(UploadWorkbookDialogOpts(action.data));
          toaster.close(toastId);
        }
      }
    });
  }

  function showConfirmOverwriteDialog() {
    ReactModalService.openCustom(OverwriteConfirmDialogOpts({
      title: Messages.contentUpload_overwriteWorkbook(),
      warningMessage: Messages.contentUpload_confirmOverwriteWarning({WORKBOOK_NAME: action.data.name}),
      onSubmit: () => {
        const overwrite = true;
        getAppStore().dispatch(requestUploadWorkbookAction({ data: action.data, overwrite: overwrite }));
      },
      onCancel: () => {
        ReactModalService.openCustom(UploadWorkbookDialogOpts(action.data));
      }
    }, 'upload-workbook-overwrite-confirmation'));
  }
  const uploadDuration = uploadEndTime - uploadStartTime;
  sendUploadWorkbookActionTelemetry(uploadDuration, fileSize, isPublishSuccessful, errorMsg);
}

export async function uploadFlow(action: UploadFlowStartAction) {
  const toaster = ToasterService.instance;

  const id = `${action.data.projectId}-${action.data.name}`;
  const progressDispatcher = generateUpdateUploadProgressHandler(id);
  const cancelToken = createCancelToken();
  const cancelHandler = () => { cancelToken.cancel('user cancelled'); };

  const projectLuid = await getProjectLuid(action.data.projectId);
  const fileSize = action.data.file.size;
  const fileName = action.data.file.name;

  const publishingMessage = Messages.contentUpload_uploadFlowNameProgressPublishing();
  const progressDialog = ReactModalService.openCustom(UploadProgressDialogOpts(id, fileSize, fileName, publishingMessage, cancelHandler));

  let isPublishSuccessful: boolean;
  let errorMsg: string;
  let uploadStartTime: any;
  let uploadEndTime: any;

  try {
    uploadStartTime = Date.now();
    const result = await UploadFlowRequestHelper.uploadFlowRequest(action, projectLuid, progressDispatcher, cancelToken);
    getAppStore().dispatch(requestUploadContentCompleteAction({ id }));
    uploadEndTime = Date.now();
    toaster.pop({
      type: ToastType.Success,
      text: Messages.flowUploadSuccessMessage(),
      keepOnLocationChange: true,
    });
    const flowUrl = generateSiteUrls().flowEditorFlowPath({flowLuid: result.flow.id});
    progressDialog.close();
    Location.instance.setUrl(flowUrl);
    getAppStore().dispatch(removeUploadContentByIdAction({ id }));
    isPublishSuccessful = true;
  } catch (error) {
    progressDialog.close();
    getAppStore().dispatch(removeUploadContentByIdAction({ id }));
    if (getErrorSourceFromRestError(error) !== RestErrorSource.Cancelled) {
      switch(error.data?.error?.code) {
        case String(UploadFlowErrorCodes.FLOW_NAME_CONFLICT):
            showFlowConfirmOverwriteDialog();
            break;
        default:
            showFlowErrorToast(error);
            break;
      }
      isPublishSuccessful = false;
    }
  }

  function showFlowErrorToast(error: any) {
    const toastId = toaster.pop({
      type: ToastType.Error,
      text: UploadFlowRequestHelper.getErrorMessage(error),
      actionMessage: UploadFlowRequestHelper.getActionMessage(error),
      action: () => {
        if (!ReactModalService.isAnyModalOpen()) {
          ReactModalService.openCustom(UploadFlowDialogOpts(action.data));
          toaster.close(toastId);
        }
      }
    });
  }

  function showFlowConfirmOverwriteDialog() {
    ReactModalService.openCustom(OverwriteConfirmDialogOpts({
      title: Messages.contentUpload_overwriteFlow(),
      warningMessage: Messages.contentUpload_confirmFlowOverwriteWarning({FLOW_NAME: action.data.name}),
      onSubmit: () => {
        const overwrite = true;
        getAppStore().dispatch(requestUploadFlowAction({ data: action.data, overwrite: overwrite }));
      },
      onCancel: () => {
        ReactModalService.openCustom(UploadFlowDialogOpts(action.data));
      }
    }, 'upload-flow-overwrite-confirmation'));
  }
  const uploadDuration = uploadEndTime - uploadStartTime;
  sendUploadFlowActionTelemetry(uploadDuration, fileSize, isPublishSuccessful, errorMsg);
}

function generateUpdateUploadProgressHandler(id: string) {
  return (progress: ProgressEvent<XMLHttpRequestUpload>) => {
    const action = requestUploadContentInProgressAction({
      id,
      bytesUploaded: progress.loaded,
      totalBytes: progress.total
    });
    // The progressHandler is not run in the saga context, so we need to use appStore instead of put
    getAppStore().dispatch(action);
  };
}

function generateSiteUrls() {
  return new SiteUrls({
    siteUrlName: SessionSiteSelectors.siteUrlName(getAppStore().getState()),
    queryString: window.location.search,
  });
}

function getDefaultViewUrl(result: WorkbookUploadResponse) {
  const defaultView = result.workbook.views.view.find((view: WorkbookView) => view.id === result.workbook.defaultViewId);
  return generateSiteUrls().viewUrl(`${result.workbook.contentUrl}/${defaultView.viewUrlName}`);
}

async function getProjectLuid(projectId: string) {
  const projectInfo = await Projects.getById(projectId).then(projectInfo => projectInfo);
  return projectInfo.luid;
}

export function parseWorkbookIdFromWebpageUrl(webpageUrl: string) {
  const urlParts = webpageUrl.split('/');
  return urlParts[urlParts.length - 1];
}

/**
 * The main saga for upload content
 */
export function* rootUploadContentSaga(): SagaIterator {
  yield takeEvery(UploadWorkbookActionsTypes.START, uploadWorkbook);
  yield takeEvery(UploadFlowActionsTypes.START, uploadFlow);
  yield takeEvery(UploadBundlesActionsTypes.START, uploadBundles);
}
